import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Settings } from 'lucide-react';
import * as Switch from '@radix-ui/react-switch';
import { DeckToolbar } from './DeckToolbar';
import { DebugToggle } from './DebugToggle';
import { ImageLibraryPanel } from './ImageLibraryPanel';
import { UrlInputPanel } from './UrlInputPanel';
import { SlideNavigation } from './SlideNavigation';
import { DeckSettings } from './DeckSettings';
import { ConfirmDialog } from './ConfirmDialog';
import { SlideCanvas } from './SlideCanvas';
import { buildApiUrl } from '../utils/apiHelpers';
import { useSlideEditorState } from './SlideEditor/useSlideEditorState';
import { useDeckData } from './SlideEditor/hooks/useDeckData';
import { useUpdateQueue } from './SlideEditor/hooks/useUpdateQueue';
import { useNodeOperations } from './SlideEditor/hooks/useNodeOperations';
import { useSlideOperations } from './SlideEditor/hooks/useSlideOperations';
import { useLockManagement } from './SlideEditor/hooks/useLockManagement';
import { useSlideNavigation } from './SlideEditor/hooks/useSlideNavigation';
import { useKeyboardShortcuts } from './SlideEditor/hooks/useKeyboardShortcuts';

export const SlideEditor: React.FC = () => {
  const { deckId, userSlugOrId, slideId: slideIdParam } = useParams<{ deckId: string; userSlugOrId: string; slideId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const state = useSlideEditorState({
    initialIsEditing: location.pathname.endsWith('/edit')
  });

  const {
    deck,
    setDeck,
    user,
    setUser,
    currentUser,
    setCurrentUser,
    slides,
    setSlides,
    currentSlideIndex,
    setCurrentSlideIndex,
    currentSlideId,
    setCurrentSlideId,
    nodes,
    setNodes,
    slideNodesCache,
    setSlideNodesCache,
    isEditing,
    setIsEditing,
    loading,
    setLoading,
    showLibrary,
    setShowLibrary,
    showUrlPanel,
    setShowUrlPanel,
    showSettings,
    setShowSettings,
    showDeleteConfirm,
    setShowDeleteConfirm,
    isAnimating,
    setIsAnimating,
    animatingNodeIds,
    setAnimatingNodeIds,
    activeNodeId,
    setActiveNodeId,
    recentImages,
    setRecentImages,
    recentUrls,
    setRecentUrls,
    updateQueue,
    setUpdateQueue,
  } = state;

  const isOwner = currentUser && deck && currentUser.id === deck.user_id;
  const canEdit = isOwner;

  const apiUrl = (path: string) => buildApiUrl(userSlugOrId!, deckId!, path);

  const deckData = useDeckData({
    deckId: deckId!,
    userSlugOrId: userSlugOrId!,
    slideIdParam,
    setDeck,
    setUser,
    setCurrentUser,
    setSlides,
    setCurrentSlideIndex,
    setCurrentSlideId,
    setNodes,
    setSlideNodesCache,
    setLoading,
  });

  const updateQueueHook = useUpdateQueue({
    currentSlideId,
    apiUrl,
    setUpdateQueue,
  });

  const nodeOps = useNodeOperations({
    currentSlideId,
    nodes,
    setNodes,
    setSlideNodesCache,
    apiUrl,
    canEdit: canEdit || false,
    setRecentImages,
    setRecentUrls,
    setShowUrlPanel,
    updateQueueHook,
  });

  const slideOps = useSlideOperations({
    deckId: deckId!,
    currentSlideId,
    currentSlideIndex,
    slides,
    setSlides,
    nodes,
    setCurrentSlideIndex,
    setIsAnimating,
    setAnimatingNodeIds,
    setShowDeleteConfirm,
    apiUrl,
    loadSlideNodes: deckData.loadSlideNodes,
    flushUpdateQueue: updateQueueHook.flushUpdateQueue,
  });

  const lockMgmt = useLockManagement({
    deckId: deckId!,
    currentSlideId,
    currentSlideIndex,
    slides,
    setSlides,
    apiUrl,
  });

  const navigation = useSlideNavigation({
    currentSlideIndex,
    slides,
    isAnimating,
    setIsAnimating,
    setCurrentSlideIndex,
    loadSlideNodes: deckData.loadSlideNodes,
    flushUpdateQueue: updateQueueHook.flushUpdateQueue,
  });

  useKeyboardShortcuts({
    isAnimating,
    setIsEditing,
    navigation,
  });

  if (!deckId || !userSlugOrId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-lg text-gray-400">Invalid deck ID</div>
      </div>
    );
  }



  useEffect(() => {
    const handleBeforeUnload = () => {
      updateQueueHook.flushUpdateQueue();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [updateQueue]);

  useEffect(() => {
    updateQueueHook.flushUpdateQueue();
  }, [isEditing]);

  // Load deck and slides data
  useEffect(() => {
    if (deckId) {
      deckData.loadDeckData();
      nodeOps.loadRecentImages();
      const savedUrls = localStorage.getItem('recentUrls');
      if (savedUrls) {
        setRecentUrls(JSON.parse(savedUrls));
      }
    }
  }, [deckId]);

  // Update URL when slide changes or edit mode changes
  useEffect(() => {
    if (!currentSlideId || !slides.length) return;

    const currentSlide = slides.find(s => s.id === currentSlideId);
    if (!currentSlide) return;

    const baseUrl = `/${userSlugOrId}/${deckId}`;
    const newPath = `${baseUrl}/${currentSlide.public_id}${isEditing ? '/edit' : ''}`;

    if (location.pathname !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [currentSlideId, slides, isEditing, location.pathname, navigate, userSlugOrId, deckId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-lg text-gray-400">Loading deck...</div>
      </div>
    );
  }

  return (
    <div
      className="slide-editor relative w-full h-screen bg-gray-900 overflow-hidden"
      onClick={(e) => {
        if ((e.target as HTMLElement).classList.contains('slide-canvas') ||
            (e.target as HTMLElement).closest('.slide-container')) {
          setActiveNodeId(null);
        }
      }}
    >
      {/* Controls */}
      {canEdit && (
        <div className="absolute top-4 left-4 right-4 z-50 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1 cursor-pointer"
          >
            <span>‹</span>
            <span>Dashboard</span>
          </button>
          <div className="flex gap-3 items-center">
            <div className="flex items-center gap-3 bg-gray-800 rounded-lg px-4 py-2 border border-gray-700">
              <span className={`text-sm ${!isEditing ? 'text-white font-medium' : 'text-gray-400'}`}>
                View
              </span>
              <Switch.Root
                checked={isEditing}
                onCheckedChange={setIsEditing}
                className="w-11 h-6 bg-gray-700 rounded-full relative data-[state=checked]:bg-blue-500 transition-colors outline-none cursor-pointer"
              >
                <Switch.Thumb className="block w-5 h-5 bg-white rounded-full transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[22px]" />
              </Switch.Root>
              <span className={`text-sm ${isEditing ? 'text-white font-medium' : 'text-gray-400'}`}>
                Edit
              </span>
            </div>
            <DebugToggle />
            <button
              onClick={() => setShowSettings(true)}
              className="px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Slide canvas */}
      <SlideCanvas
        slides={slides}
        slideNodesCache={slideNodesCache}
        currentSlideIndex={currentSlideIndex}
        isEditing={isEditing}
        animatingNodeIds={animatingNodeIds}
        activeNodeId={activeNodeId}
        setActiveNodeId={setActiveNodeId}
        nodeOps={nodeOps}
      />

      {/* Slide Navigation */}
      {slides.length > 1 && (
        <SlideNavigation
          totalSlides={slides.length}
          currentSlideIndex={currentSlideIndex}
          onNavigate={navigation.goToSlide}
        />
      )}

      {/* Deck Toolbar */}
      {isEditing && (
        <DeckToolbar
          onAddText={nodeOps.addTextNode}
          onImageUpload={nodeOps.addImageNode}
          onSelectRecentImage={nodeOps.addImageFromUrl}
          onSelectFromLibrary={() => setShowLibrary(true)}
          onSelectEmoji={() => console.log('Select emoji')}
          onAddLink={() => setShowUrlPanel(true)}
          onToggleLock={lockMgmt.toggleSlideLock}
          onLockWithUnlockSlide={lockMgmt.lockSlideWithUnlockSetting}
          onNewSlide={slideOps.addNewSlide}
          onCopySlide={slideOps.copyCurrentSlide}
          onDeleteSlide={slideOps.confirmDeleteSlide}
          isLocked={slides[currentSlideIndex]?.locked || false}
          lockPosition={slides[currentSlideIndex]?.lock_position}
          unlockOnSlide={slides[currentSlideIndex]?.unlock_on_slide}
          currentSlideIndex={currentSlideIndex}
          totalSlides={slides.length}
          onLockPositionChange={lockMgmt.updateLockPosition}
          onUnlockSlideChange={lockMgmt.updateUnlockSlide}
          recentImages={recentImages}
        />
      )}

      {/* URL Input Panel */}
      {showUrlPanel && (
        <UrlInputPanel
          onSubmit={nodeOps.addIframeNode}
          onClose={() => setShowUrlPanel(false)}
          recentUrls={recentUrls}
        />
      )}

      {/* Image Library Panel */}
      {showLibrary && (
        <ImageLibraryPanel
          onSelectImage={(url) => {
            nodeOps.addImageFromUrl(url);
            setShowLibrary(false);
          }}
          onClose={() => setShowLibrary(false)}
        />
      )}

      {/* Deck Settings */}
      {showSettings && deck && user && (
        <DeckSettings
          open={showSettings}
          onOpenChange={setShowSettings}
          deck={deck}
          user={user}
          onUpdate={(updatedDeck) => {
            setDeck(updatedDeck);
            const oldSlug = deck.slug || deck.public_id;
            const newSlug = updatedDeck.slug || updatedDeck.public_id;
            if (oldSlug !== newSlug && user.slug) {
              navigate(`/${user.slug}/${newSlug}`, { replace: true });
              window.location.reload();
            }
          }}
          onSlugChange={(newSlug) => {
            if (user.slug) {
              navigate(`/${user.slug}/${newSlug}`, { replace: true });
              window.location.reload();
            }
          }}
        />
      )}

      {/* Delete Slide Confirmation */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete slide"
        description={slides.length <= 1 ? "Cannot delete the last slide" : "Are you sure you want to delete this slide?"}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={slideOps.deleteCurrentSlide}
        variant="danger"
      />
    </div>
  );
};